import { resolveArchivedInlineParts } from "../mail/archivedInlineParts";
import * as React from "react";
import { Button, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import {
    ArrowDownloadRegular,
    ArrowForwardRegular,
    ArrowReplyAllRegular,
    ArrowReplyRegular,
    CheckmarkCircleFilled,
    FolderArrowRightRegular,
    ImageOffRegular,
    PrintRegular,
} from "@fluentui/react-icons";
import { SandboxedMailBody } from "../mail/SandboxedMailBody";
import { useMailBildAktionen } from "../mail/useMailBildAktionen";
import { cidSchluessel } from "../../services/graphMailService";
import { baueDruckDokument, druckeHtml, type DruckNachricht } from "../mail/mailDruck";
import { hinweisen } from "../redesign/bestaetigen";
import { FileIcon } from "./explorer/fileTypeIcons";
import { speichereMail } from "../mail/mailHerunterladen";
import {
    mailFileService,
    type MailFileAddress,
    type MailFileAttachment,
    type ParsedMailFile,
} from "../../services/mailFileService";

/**
 * Ansicht für archivierte Mail-Dateien (`.msg` / `.eml`) aus der Kundenakte.
 *
 * BEWUSST NICHT `MailDetailWindow`: das Graph-Fenster ist auf ein lebendes Postfach
 * ausgelegt (Zuweisen, Ordner, Lesestatus, Antworten im selben Gesprächsverlauf).
 * Eine Archivdatei hat davon nichts — die halbe Bedienoberfläche wäre wirkungslos.
 * Geteilt wird das Aussehen und der `SandboxedMailBody`, nicht die Logik.
 *
 * Antworten/Weiterleiten (T-247) gibt es trotzdem, wenn der Aufrufer `aktionen`
 * mitgibt: Die Datei bleibt Archiv, die Antwort ist eine NEUE Mail mit „AW:" und
 * Zitat (ohne technische Verkettung beim Empfänger — die Datei kennt keine
 * Postfach-Nachricht, auf die sie sich beziehen könnte).
 */

/** Antworten/Allen antworten/Weiterleiten — je mit der UNGERENDERTEN Mail (ohne `blob:`-Bilder). */
export type MailDateiAktionen = {
    onAntworten: (mail: ParsedMailFile) => void;
    onAllenAntworten: (mail: ParsedMailFile) => void;
    /**
     * Fehlt, wenn die Datei kein KMVS-Dokument ist (Kundenakten-Ablage, T-219):
     * Weiterleiten hängt die Datei als Dokument an.
     */
    onWeiterleiten?: (mail: ParsedMailFile) => void;
};

const useStyles = makeStyles({
    root: {
        height: "100%",
        overflowY: "auto",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    center: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        textAlign: "center",
        color: tokens.colorNeutralForeground3,
    },
    header: {
        padding: "18px 22px 14px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    subject: {
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "24px",
        marginBottom: "12px",
        color: tokens.colorNeutralForeground1,
        wordBreak: "break-word",
    },
    row: {
        display: "flex",
        gap: "8px",
        fontSize: "13px",
        lineHeight: "20px",
        marginTop: "2px",
    },
    label: {
        minWidth: "58px",
        flexShrink: 0,
        color: tokens.colorNeutralForeground3,
    },
    value: {
        color: tokens.colorNeutralForeground1,
        wordBreak: "break-word",
    },
    banner: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        margin: "12px 22px 0",
        padding: "8px 12px",
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        fontSize: "13px",
        color: tokens.colorNeutralForeground2,
    },
    warning: {
        margin: "12px 22px 0",
        padding: "8px 12px",
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorPaletteYellowBackground2,
        color: tokens.colorNeutralForeground1,
        fontSize: "13px",
    },
    body: {
        padding: "16px 22px",
    },
    attachments: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: "14px 22px 20px",
    },
    attachmentsTitle: {
        fontSize: "12px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        color: tokens.colorNeutralForeground3,
        marginBottom: "10px",
    },
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
    },
    chip: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 8px 6px 10px",
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
        maxWidth: "320px",
    },
    done: {
        color: tokens.colorPaletteGreenForeground1,
        display: "flex",
        alignItems: "center",
    },
    chipText: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
    chipName: {
        fontSize: "13px",
        color: tokens.colorNeutralForeground1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    chipMeta: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
    },
});

/** Kompakte Größenangabe; die Anhangleiste hat wenig Platz. */
const formatSize = (bytes: number): string => {
    if (!bytes || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** „Max Muster <max@example.org>" bzw. nur das, was vorhanden ist. */
const formatAddress = (a: MailFileAddress): string => {
    if (a.name && a.address) return `${a.name} <${a.address}>`;
    return a.name || a.address;
};

const formatAddresses = (list: MailFileAddress[]): string =>
    list.map(formatAddress).join("; ");

const formatDate = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/**
 * Woher die Mail kommt. Ohne Angabe ist es ein KMVS-Dokument (`docId`); die
 * Kundenakten-Ablage (Plan 01.10.2026, B2) reicht stattdessen ihre Endpunkte
 * durch — dieselbe Ansicht, aber ohne Dokument-Id und ohne „in die Akte
 * übernehmen" (es gibt noch keine Akte, in die es ginge).
 */
export type MailQuelle = {
    /** Stabiler Schlüssel; ein Wechsel lädt neu. */
    key: string;
    fetchParsed: () => Promise<ParsedMailFile>;
    fetchAttachmentBlob: (index: number) => Promise<Blob>;
    /** Fehlt es, gibt es den Übernehmen-Knopf nicht. */
    importAttachment?: (index: number) => Promise<unknown>;
    /** Die Mail als `.eml` — fehlt es, gibt es keinen Herunterladen-Knopf neben „Drucken". */
    fetchOriginal?: () => Promise<Blob>;
};

export const MailFileViewer: React.FC<{
    docId?: number;
    quelle?: MailQuelle;
    /**
     * Die gesicherte Kopie einer verknüpften Postfach-Mail: sie soll aussehen wie
     * im Postfach — Bilder von außen laden sofort, technische Hinweise zur
     * Darstellung entfallen. Für abgelegte Mail-DATEIEN (fremde Herkunft) bleibt
     * es bei der Blockade mit „Trotzdem anzeigen".
     */
    wieImPostfach?: boolean;
    /** T-247: Knöpfe „Antworten", „Allen antworten", „Weiterleiten" im Kopf. */
    aktionen?: MailDateiAktionen;
}> = ({ docId, quelle, wieImPostfach = false, aktionen }) => {
    const styles = useStyles();
    const q = React.useMemo<MailQuelle>(
        () =>
            quelle ?? {
                key: `doc-${docId}`,
                fetchParsed: () => mailFileService.fetchParsed(docId as number),
                fetchAttachmentBlob: (index) => mailFileService.fetchAttachmentBlob(docId as number, index),
                importAttachment: (index) => mailFileService.importAttachment(docId as number, index),
            },
        [docId, quelle],
    );
    const [mail, setMail] = React.useState<ParsedMailFile | null>(null);
    // Die Mail so, wie der Server sie lieferte — für Antwort und Zitat. Die
    // angezeigte Fassung trägt `blob:`-Adressen für Inline-Bilder, die es beim
    // Empfänger nicht gibt.
    const rohRef = React.useRef<ParsedMailFile | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    // Externe Inhalte bleiben blockiert, bis der Nutzer sie ausdrücklich anfordert
    // (Zählpixel melden dem Absender sonst, wann wer die Mail geöffnet hat).
    const [showRemote, setShowRemote] = React.useState(wieImPostfach);
    const [hasRemote, setHasRemote] = React.useState(false);
    // T-236: Klick aufs Bild im Text = Vorschau, Rechtsklick = eigenes Menü.
    const { beiBild, bildMenue } = useMailBildAktionen({
        mailKey: q.key,
        datum: mail?.date ? new Date(mail.date) : null,
        findeAnhang: (cid) => {
            if (!cid) return null;
            const a = mail?.attachments.find((x) => x.isInline && x.contentId && cidSchluessel(x.contentId) === cid);
            return a ? { name: a.name, contentType: a.contentType } : null;
        },
    });

    React.useEffect(() => {
        let alive = true;
        let dispose: (() => void) | undefined;
        setMail(null);
        setImported({});
        setError(null);
        setShowRemote(wieImPostfach);
        setHasRemote(false);
        rohRef.current = null;
        q.fetchParsed()
            .then(async (parsed) => {
                if (alive) rohRef.current = parsed;
                const resolved = await resolveArchivedInlineParts(parsed.bodyHtml, parsed.inlineParts ?? [], q.fetchAttachmentBlob);
                if (!alive) { resolved.dispose(); return; }
                dispose = resolved.dispose;
                setMail({ ...parsed, bodyHtml: resolved.html, warnings: [...parsed.warnings, ...resolved.warnings] });
            })
            .catch(() => { if (alive) setError("Die Mail-Datei konnte nicht gelesen werden."); });
        return () => { alive = false; dispose?.(); };
        // Die Identität der Mail hängt am Schlüssel, nicht an den Funktionsreferenzen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q.key]);

    // Übernahme-Zustand je Anhang-Index. `done` bleibt nur für die Sitzung stehen —
    // der Endpunkt ist idempotent, ein erneuter Klick legt nichts doppelt an.
    const [imported, setImported] = React.useState<Record<number, "busy" | "done">>({});

    const downloadAttachment = React.useCallback(
        async (att: MailFileAttachment) => {
            try {
                const blob = await q.fetchAttachmentBlob(att.index);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = att.name;
                a.click();
                // Erst nach dem Klick freigeben — sonst bricht der Download in Safari ab.
                setTimeout(() => URL.revokeObjectURL(url), 10_000);
            } catch {
                setError("Der Anhang konnte nicht geladen werden.");
            }
        },
        [q],
    );

    const importAttachment = React.useCallback(
        async (att: MailFileAttachment) => {
            if (!q.importAttachment) return;
            setImported((prev) => ({ ...prev, [att.index]: "busy" }));
            try {
                await q.importAttachment(att.index);
                setImported((prev) => ({ ...prev, [att.index]: "done" }));
            } catch {
                setImported((prev) => {
                    const next = { ...prev };
                    delete next[att.index];
                    return next;
                });
                setError("Der Anhang konnte nicht übernommen werden.");
            }
        },
        [q],
    );

    if (error) return <div className={styles.center}>{error}</div>;
    if (!mail) {
        return (
            <div className={styles.center}>
                <Spinner size="small" label="Mail wird gelesen…" />
            </div>
        );
    }

    // Inline-Bilder gehören in den Body, nicht in die Anhangleiste.
    const visibleAttachments = mail.attachments.filter((a) => !a.isInline);

    const herunterladen = () => {
        quelle?.fetchOriginal?.()
            .then((blob) => speichereMail(blob, mail.subject))
            .catch(() => {
                void hinweisen({
                    titel: "Herunterladen nicht möglich",
                    text: "Die Mail konnte nicht heruntergeladen werden. Bitte erneut versuchen.",
                });
            });
    };

    const drucken = () => {
        const nachricht: DruckNachricht = {
            betreff: mail.subject || "",
            von: mail.from ?? null,
            an: mail.to,
            cc: mail.cc,
            bcc: mail.bcc,
            datum: mail.date ?? null,
            bodyHtml: mail.bodyHtml,
            anhaenge: visibleAttachments.map((a) => ({ name: a.name, groesse: a.size })),
        };
        druckeHtml(baueDruckDokument([nachricht], { blockRemote: !showRemote })).catch(() => {
            void hinweisen({
                titel: "Drucken nicht möglich",
                text: "Die Mail konnte nicht gedruckt werden. Bitte erneut versuchen.",
            });
        });
    };

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <div className={styles.subject} style={{ flex: 1, minWidth: 0 }}>{mail.subject || "(Kein Betreff)"}</div>
                    {/* T-195 — kleiner Drucker-Knopf: druckt Kopf + Text, Anhänge als Liste. */}
                    {aktionen && (
                        <>
                            <Button
                                appearance="subtle"
                                size="small"
                                icon={<ArrowReplyRegular />}
                                title="Antworten"
                                aria-label="Antworten"
                                onClick={() => aktionen.onAntworten(rohRef.current ?? mail)}
                                style={{ flexShrink: 0 }}
                            />
                            <Button
                                appearance="subtle"
                                size="small"
                                icon={<ArrowReplyAllRegular />}
                                title="Allen antworten"
                                aria-label="Allen antworten"
                                onClick={() => aktionen.onAllenAntworten(rohRef.current ?? mail)}
                                style={{ flexShrink: 0 }}
                            />
                            {aktionen.onWeiterleiten && (
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<ArrowForwardRegular />}
                                    title="Weiterleiten"
                                    aria-label="Weiterleiten"
                                    onClick={() => aktionen.onWeiterleiten?.(rohRef.current ?? mail)}
                                    style={{ flexShrink: 0 }}
                                />
                            )}
                        </>
                    )}
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<PrintRegular />}
                        title="Drucken"
                        aria-label="Drucken"
                        onClick={drucken}
                        style={{ flexShrink: 0 }}
                    />
                    {quelle?.fetchOriginal && (
                        <Button
                            appearance="subtle"
                            size="small"
                            icon={<ArrowDownloadRegular />}
                            title="Herunterladen (.eml)"
                            aria-label="Herunterladen"
                            onClick={herunterladen}
                            style={{ flexShrink: 0 }}
                        />
                    )}
                </div>
                <div className={styles.row}>
                    <span className={styles.label}>Von</span>
                    <span className={styles.value}>
                        {mail.from ? formatAddress(mail.from) : "(Unbekannt)"}
                    </span>
                </div>
                {mail.to.length > 0 && (
                    <div className={styles.row}>
                        <span className={styles.label}>An</span>
                        <span className={styles.value}>{formatAddresses(mail.to)}</span>
                    </div>
                )}
                {mail.cc.length > 0 && (
                    <div className={styles.row}>
                        <span className={styles.label}>Cc</span>
                        <span className={styles.value}>{formatAddresses(mail.cc)}</span>
                    </div>
                )}
                {mail.bcc.length > 0 && (
                    <div className={styles.row}>
                        <span className={styles.label}>Bcc</span>
                        <span className={styles.value}>{formatAddresses(mail.bcc)}</span>
                    </div>
                )}
                {mail.date && (
                    <div className={styles.row}>
                        <span className={styles.label}>Datum</span>
                        <span className={styles.value}>{formatDate(mail.date)}</span>
                    </div>
                )}
            </div>

            {!wieImPostfach && mail.warnings.map((w, i) => (
                <div key={i} className={styles.warning} role="status">{w}</div>
            ))}

            {hasRemote && !showRemote && (
                <div className={styles.banner} role="status">
                    <ImageOffRegular fontSize={16} />
                    <span style={{ flex: 1 }}>
                        Externer Inhalt, nicht mitgesichert. Externe Inhalte wurden blockiert. Sie können dem Absender melden, dass Sie
                        diese Mail geöffnet haben.
                    </span>
                    <Button size="small" appearance="secondary" onClick={() => setShowRemote(true)}>
                        Trotzdem anzeigen
                    </Button>
                </div>
            )}

            <SandboxedMailBody
                className={styles.body}
                html={mail.bodyHtml}
                blockRemote={!showRemote}
                onRemotePresence={(present) => { if (present) setHasRemote(true); }}
                onBild={beiBild}
            />
            {bildMenue}

            {visibleAttachments.length > 0 && (
                <div className={styles.attachments}>
                    <div className={styles.attachmentsTitle}>
                        {visibleAttachments.length} Anhang{visibleAttachments.length === 1 ? "" : "änge"}
                    </div>
                    <div className={styles.chips}>
                        {visibleAttachments.map((att) => {
                            const state = imported[att.index];
                            return (
                                <div key={att.index} className={styles.chip}>
                                    <FileIcon fileName={att.name} mimeType={att.contentType} size={20} />
                                    <span className={styles.chipText}>
                                        <span className={styles.chipName} title={att.name}>{att.name}</span>
                                        <span className={styles.chipMeta}>
                                            {formatSize(att.size)}
                                            {att.isMail ? " · E-Mail" : ""}
                                            {state === "done" ? " · in der Akte" : ""}
                                        </span>
                                    </span>
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<ArrowDownloadRegular />}
                                        title={`${att.name} herunterladen`}
                                        aria-label={`${att.name} herunterladen`}
                                        onClick={() => void downloadAttachment(att)}
                                    />
                                    {state === "done" ? (
                                        <span className={styles.done} title="Als Dokument in der Akte abgelegt">
                                            <CheckmarkCircleFilled fontSize={20} />
                                        </span>
                                    ) : !q.importAttachment ? null : (
                                        <Button
                                            appearance="subtle"
                                            size="small"
                                            disabled={state === "busy"}
                                            icon={<FolderArrowRightRegular />}
                                            title="In die Akte übernehmen"
                                            aria-label={`${att.name} in die Akte übernehmen`}
                                            onClick={() => void importAttachment(att)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
